import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { hojeBR } from '../utils/data';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { expandirItemContagem, somaVencimentos } from '../utils/vencimentosContagem';
import s from './PedidosOrcamentos.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';
import useNavegacaoLista from '../hooks/useNavegacaoLista';
import { useRolarLinha } from '../hooks/useNavegacaoLista';

const FILTROS_VAZIOS = { codigo: '', descricao: '', ver_inativos: false };
const today = () => hojeBR();

const FORM_VAZIO = {
  id: null,
  data: today(),
  setor_id: '',
  motivo: 'Contagem de estoque',
  itens: [],
};

const fmtData = str =>
  str ? new Date(String(str).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

const fmtHora = iso => iso ? new Date(iso).toLocaleString('pt-BR') : '—';

const fmtQtd = (v, fraciona = false) =>
  v != null ? Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: fraciona ? 3 : 0, maximumFractionDigits: fraciona ? 3 : 0,
  }) : '—';

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

async function fetchEstoqueLocal(produto_id, setor_id) {
  if (!produto_id || !setor_id) return null;
  try {
    const { data } = await api.get(`/estoque/produto/${produto_id}/setores`);
    const se = data.find(s => String(s.setor_id) === String(setor_id));
    return se ? parseFloat(se.quantidade) : 0;
  } catch { return null; }
}

// Casa um código digitado/escaneado com um produto: prioriza igualdade exata
// (código de barras ou código interno) — busca textual (nome) fica só como
// fallback quando existe exatamente 1 resultado, pra não confundir um scan
// de código com uma busca por nome parcial.
// inativo:'true' inclui produtos inativos na busca — a busca por padrão
// ignora inativos (produtosController.listar), então sem isso um código
// escaneado de um produto inativo simplesmente "não seria encontrado" em
// vez de dar chance de reativar (ver confirmarReativacaoSeInativo abaixo).
async function resolverProdutoPorCodigo(codigo) {
  const cod = codigo.trim();
  if (!cod) return null;
  const { data } = await api.get('/produtos', { params: { busca: cod, limite: 8, inativo: 'true' } });
  if (!data.length) return null;
  const exato = data.find(p => p.codigo_barras === cod || String(p.codigo_interno) === cod);
  if (exato) return exato;
  return data.length === 1 ? data[0] : null;
}

// Produto encontrado, mas marcado INATIVO no cadastro — pergunta se quer
// ativar antes de deixar seguir com a Contagem. Devolve o produto (já
// ativo) se confirmou, ou null se recusou — o chamador trata null como
// "não achou" (mesmo destino de um código não cadastrado).
async function confirmarReativacaoSeInativo(p) {
  if (!p) return p;
  // ativo=false é o botão "Excluir" (soft-delete) — mais sério que o
  // checkbox "Produto inativo"; alteração em massa não reativa isso
  // (a query dela exige ativo=true pra aceitar a alteração), então nem
  // oferece — só avisa e manda pro Cad. Produtos.
  if (p.ativo === false) {
    alert(`O produto "${p.nome}" foi EXCLUÍDO do cadastro — não dá pra usar por aqui.\n\nPeça pra um administrador restaurá-lo em Cad. Produtos.`);
    return null;
  }
  if (!p.inativo) return p; // já ativo, segue normal
  if (!window.confirm(`O produto "${p.nome}" está INATIVO.\n\nDeseja ativá-lo agora para continuar?`)) return null;
  try {
    await api.put('/produtos/alteracao-massa', { ids: [p.id], alteracoes: { produto: { inativo: false } } });
    return { ...p, inativo: false, ativo: true };
  } catch (err) {
    alert(err.response?.data?.erro || 'Não foi possível ativar o produto — tente de novo.');
    return null;
  }
}

/* ══════════════════════════════════════════════════════════
   Modal F3 — Busca Avançada de Produtos
   ══════════════════════════════════════════════════════════ */
function ModalBuscaProduto({ onSelect, onClose, consultaOnly = false }) {
  const [filtros, setFiltros]         = useState(FILTROS_VAZIOS);
  const [produtos, setProdutos]       = useState([]);
  const [buscando, setBuscando]       = useState(false);
  const [idx, setIdx]                 = useState(null);
  const refLinhaProd = useRolarLinha(idx);
  const [buscaRapida, setBuscaRapida] = useState('');
  const buscaRapidaRef = useRef(null);
  const debRef         = useRef(null);

  useEffect(() => { setTimeout(() => buscaRapidaRef.current?.focus(), 60); }, []);

  const buscar = async (flt, rapida = '') => {
    setBuscando(true); setIdx(null);
    try {
      const params = { limite: 200 };
      if (rapida)           params.busca   = rapida;
      if (flt.codigo)       params.busca   = flt.codigo;
      if (flt.descricao)    params.busca   = flt.descricao;
      if (flt.ver_inativos) params.inativo = 'true';
      const { data } = await api.get('/produtos', { params });
      setProdutos(Array.isArray(data) ? data : []);
    } catch { setProdutos([]); }
    finally  { setBuscando(false); }
  };

  const handleRapida = e => {
    const v = e.target.value;
    setBuscaRapida(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => buscar(filtros, v), 300);
  };

  const setFiltro  = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }));
  const confirmar  = p => { if (p && !consultaOnly) onSelect(p); };

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Enter' && idx !== null && !consultaOnly) { e.preventDefault(); confirmar(produtos[idx]); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => i === null ? 0 : Math.min(i + 1, produtos.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => i === null ? 0 : Math.max(i - 1, 0)); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [idx, produtos]); // eslint-disable-line

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modalF3} onClick={e => e.stopPropagation()}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{consultaOnly ? 'Consulta de Produtos' : 'F3/F9 — Busca Avançada de Produtos'}</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
            onClick={onClose}>✕</button>
        </div>

        <div className={s.f3Filtros}>
          <div className={s.f3FiltroGrid}>
            {[['Código', 'codigo'], ['Descrição', 'descricao']].map(([label, campo]) => (
              <div key={campo} className={s.f3Campo}>
                <label className={s.f3Label}>{label}:</label>
                <input className={s.f3Input} value={filtros[campo]}
                  onChange={e => setFiltro(campo, e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && buscar(filtros, buscaRapida)} />
              </div>
            ))}
          </div>
          <div className={s.f3Checks}>
            <label className={s.f3CheckLabel}>
              <input type="checkbox" checked={filtros.ver_inativos}
                onChange={e => setFiltro('ver_inativos', e.target.checked)} />
              Ver inativos
            </label>
          </div>
          <div className={s.f3FiltrarRow}>
            <button className={s.f3BtnFiltrar} onClick={() => buscar(filtros, buscaRapida)}>Filtrar</button>
            <button className={s.f3BtnLimpar}
              onClick={() => { setFiltros(FILTROS_VAZIOS); setProdutos([]); setBuscaRapida(''); }}>Limpar</button>
            {buscando && <span className={s.f3Buscando}>Buscando…</span>}
          </div>
        </div>

        <div className={s.f3GridWrap}>
          <table className={s.f3Grid}>
            <thead>
              <tr>
                <th style={{ width: 85 }}>Código</th>
                <th>Descrição</th>
                <th style={{ width: 42 }}>UN</th>
                <th style={{ width: 90, textAlign: 'right' }}>Estoque</th>
              </tr>
            </thead>
            <tbody>
              {produtos.length === 0 && !buscando && (
                <tr><td colSpan={4} className={s.f3Vazio}>Use os filtros acima ou a busca rápida abaixo.</td></tr>
              )}
              {produtos.map((p, i) => (
                <tr key={p.id}
                  ref={idx === i ? refLinhaProd : null}
                  className={idx === i ? s.f3LinhaSel : ''}
                  onClick={() => setIdx(i)}
                  onDoubleClick={() => confirmar(p)}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.codigo_interno}</td>
                  <td>{p.nome}{p.composto ? ' 🧩' : ''}</td>
                  <td>{p.unidade || ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    {fmtQtd(p.estoque_total || 0, p.unidade_fraciona)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={s.f3Rodape}>
          <div className={s.f3BuscaRapidaWrap}>
            <label className={s.f3Label} style={{ whiteSpace: 'nowrap' }}>Busca rápida:</label>
            <input ref={buscaRapidaRef} className={s.f3BuscaRapida}
              placeholder="Nome ou código…" value={buscaRapida} onChange={handleRapida} autoComplete="off" />
          </div>
          <div className={s.f3BotoesRodape}>
            {!consultaOnly && (
              <button className={s.f3BtnOk} disabled={idx === null}
                onClick={() => idx !== null && confirmar(produtos[idx])}>Ok</button>
            )}
            <button className={s.f3BtnSair} onClick={onClose}>Sair</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Modal Busca de Manutenção
   ══════════════════════════════════════════════════════════ */
function ModalBuscaManutencao({ onSelect, onClose }) {
  const [busca, setBusca]           = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim]       = useState('');
  const [lista, setLista]           = useState([]);
  const [sel, setSel]               = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    api.get('/estoque/manutencao').then(r => setLista(r.data)).catch(() => {});
  }, []);

  const filtrada = lista.filter(m => {
    const b = busca.toLowerCase();
    if (b && !m.motivo.toLowerCase().includes(b) && !(m.setor_nome || '').toLowerCase().includes(b)) return false;
    if (dataInicio && m.data < dataInicio) return false;
    if (dataFim    && m.data > dataFim)    return false;
    return true;
  });

  const refLinha = useNavegacaoLista({
    lista: filtrada, indice: filtrada.findIndex(m => m.id === sel?.id), aoMudar: setSel, aoEnter: onSelect,
  });

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ maxWidth: 720 }}>
        <div className={s.modalTitle}>Buscar Manutenção de Estoque</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow} style={{ flexWrap: 'wrap', gap: 6 }}>
            <input ref={inputRef} className={s.inputErp} style={{ flex: 1, minWidth: 160 }}
              placeholder="Motivo ou local..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && onClose()} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
              De:
              <InputDataFiltro className={s.inputErp} style={{ width: 130 }}
                value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
              Até:
              <InputDataFiltro className={s.inputErp} style={{ width: 130 }}
                value={dataFim} onChange={e => setDataFim(e.target.value)} />
            </label>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className={s.modalTabela}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: 90 }}>Data</th>
                  <th style={{ width: 80 }}>Local</th>
                  <th>Motivo</th>
                  <th style={{ width: 50, textAlign: 'center' }}>Itens</th>
                  <th style={{ width: 100 }}>Usuário</th>
                </tr>
              </thead>
              <tbody>
                {filtrada.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 10, color: '#888', textAlign: 'center' }}>
                    Nenhum ajuste encontrado.
                  </td></tr>
                )}
                {filtrada.map(m => (
                  <tr key={m.id} ref={sel?.id === m.id ? refLinha : null} className={sel?.id === m.id ? s.selRow : ''}
                    onClick={() => setSel(m)} onDoubleClick={() => onSelect(m)}>
                    <td style={{ fontWeight: 'bold' }}>{m.id}</td>
                    <td>{fmtData(m.data)}</td>
                    <td>{m.setor_sigla ? `${m.setor_sigla} — ${m.setor_nome}` : '—'}</td>
                    <td>{m.motivo}</td>
                    <td style={{ textAlign: 'center' }}>{m.total_itens}</td>
                    <td>{m.usuario_nome || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => sel && onSelect(sel)} disabled={!sel}>Selecionar</button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Modal — Diferença em produto composto (kit/montado)
   ══════════════════════════════════════════════════════════ */
function ModalResolverComposto({ item, onResolver, onClose }) {
  const diferenca = item.contada - item.estoque;
  const sobra = diferenca > 0;
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');

  const executar = async (modo) => {
    setProcessando(true); setErro('');
    try { await onResolver(modo); }
    catch (err) { setErro(err?.response?.data?.erro || 'Erro ao processar.'); }
    finally { setProcessando(false); }
  };

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ maxWidth: 460 }}>
        <div className={s.modalTitle}>🧩 Diferença em produto composto</div>
        <div className={s.modalBody}>
          <p style={{ margin: '0 0 8px' }}>
            <strong>{item.descricao}</strong> é um produto composto (kit/montado).
          </p>
          <p style={{ margin: '0 0 10px' }}>
            A contagem encontrou{' '}
            <strong style={{ color: sobra ? '#006600' : '#c00000' }}>
              {sobra ? `${fmtQtd(diferenca)} a mais` : `${fmtQtd(Math.abs(diferenca))} a menos`}
            </strong>{' '}
            do que o sistema tinha registrado.
          </p>
          {erro && (
            <p style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4,
              padding: '6px 10px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>{erro}</p>
          )}
          <p style={{ fontSize: 12, color: '#555', margin: 0 }}>
            {sobra
              ? <><strong>Montar automático</strong> registra a montagem dessas unidades extras, consumindo os componentes da ficha técnica do estoque. <strong>Ajuste direto</strong> só soma no composto, sem mexer nos componentes.</>
              : <><strong>Desmontar automático</strong> registra a desmontagem, devolvendo os componentes ao estoque (assume que os kits foram desmontados, não perdidos). <strong>Ajuste direto</strong> só desconta do composto, sem devolver componentes (assume perda/extravio).</>}
          </p>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} disabled={processando} onClick={() => executar('direto')}>
            Ajuste direto
          </button>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={processando}
            onClick={() => executar(sobra ? 'montar' : 'desmontar')}>
            {processando ? 'Processando…' : sobra ? '🔧 Montar automático' : '🔧 Desmontar automático'}
          </button>
          <button className={s.btnErp} onClick={onClose} disabled={processando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// Detalha a diferença de ENTRADA de um item em mais de um vencimento —
// produto com lotes de validade diferentes contados juntos (ver
// utils/vencimentosContagem.js pro porquê disso não existir pra saída).
function ModalVencimentos({ item, onSalvar, onClose }) {
  const diferenca = item.contada - item.estoque;
  const [linhas, setLinhas] = useState(item.vencimentos?.length ? item.vencimentos : [{ quantidade: '', data_vencimento: '' }]);

  const alterar = (idx, campo, valor) => setLinhas(prev => prev.map((v, i) => i === idx ? { ...v, [campo]: valor } : v));
  const remover = (idx) => setLinhas(prev => prev.filter((_, i) => i !== idx));
  const adicionar = () => setLinhas(prev => [...prev, { quantidade: '', data_vencimento: '' }]);

  const soma = somaVencimentos(linhas);
  const passouDoLimite = soma > item.contada;

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ maxWidth: 480 }}>
        <div className={s.modalTitle}>📅 Vencimentos — {item.descricao}</div>
        <div className={s.modalBody}>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#555' }}>
            Total contado: <strong>{fmtQtd(item.contada)}</strong> (diferença desta contagem:{' '}
            <strong style={{ color: '#006600' }}>+{fmtQtd(diferenca)}</strong>) — detalhe a composição do total
            contado por vencimento, não só da diferença (ex.: já tinha 40, contou 50 — os 50 podem estar em
            vencimentos diferentes). O que declarar aqui <strong>substitui</strong> os vencimentos já registrados
            desse produto; deixe em branco pra não mexer no que já estava lá.
          </p>
          {linhas.map((v, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input className={s.inputErp} style={{ width: 90 }} type="number" min="0" step="0.001"
                placeholder="Qtd." value={v.quantidade} onChange={e => alterar(idx, 'quantidade', e.target.value)} />
              <input className={s.inputErp} style={{ flex: 1 }} type="date"
                value={v.data_vencimento} onChange={e => alterar(idx, 'data_vencimento', e.target.value)} />
              <button className={s.btnErp} style={{ padding: '2px 8px' }} onClick={() => remover(idx)} title="Remover">✕</button>
            </div>
          ))}
          {passouDoLimite && (
            <p style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4,
              padding: '6px 10px', fontSize: 12, color: '#b91c1c', margin: '6px 0' }}>
              A soma ({fmtQtd(soma)}) não pode passar do total contado ({fmtQtd(item.contada)}).
            </p>
          )}
          <button className={s.btnErp} onClick={adicionar}>➕ Adicionar vencimento</button>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={passouDoLimite}
            onClick={() => onSalvar(linhas.filter(v => parseFloat(v.quantidade) > 0))}>
            Salvar
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Componente principal
   ══════════════════════════════════════════════════════════ */
export default function ManutencaoEstoque() {
  const [lista, setLista]         = useState([]);
  const [posicao, setPosicao]     = useState(0);
  const [form, setForm]           = useState({ ...FORM_VAZIO });
  const [modo, setModo]           = useState('nav');
  const [erroSalvar, setErroSalvar] = useState('');
  const [salvando, setSalvando]   = useState(false);
  const [setoresLista, setSetoresLista] = useState([]);

  const [modalBusca, setModalBusca]       = useState(false);
  const [modalProdutoDigitacao, setModalProdutoDigitacao] = useState(false); // F9 na digitação
  const [modalConsulta, setModalConsulta] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const [modalResolverComposto, setModalResolverComposto] = useState(null); // { idx }
  const [modalVencimentos, setModalVencimentos] = useState(null); // { idx }

  // ── Painel de digitação (contagem) ──
  const [abaContagem, setAbaContagem]   = useState('contados'); // 'contados' | 'nao_listados'
  const [qtd1Auto, setQtd1Auto]         = useState(false);
  const [solicitarSecao, setSolicitarSecao] = useState(false);
  const [codigoInput, setCodigoInput]   = useState('');
  const [produtoPendente, setProdutoPendente] = useState(null); // produto resolvido, aguardando quantidade
  const [quantidadeInput, setQuantidadeInput] = useState('');
  const [secaoPendente, setSecaoPendente] = useState('');
  const [buscandoCodigo, setBuscandoCodigo] = useState(false);
  const [naoListados, setNaoListados]   = useState([]); // [{codigo, data_hora}] — só na sessão, não persistido

  const codigoInputRef     = useRef(null);
  const quantidadeInputRef = useRef(null);
  const keySeq = useRef(1000);
  const editando = modo !== 'nav';
  const podeDigitar = editando && (modo === 'novo' || modo === 'alterar');

  /* Carrega setores */
  useEffect(() => {
    api.get('/setores-estoque').then(r => setSetoresLista(r.data)).catch(() => {});
  }, []);

  /* Foco automático no campo de código quando o painel de digitação liga */
  useEffect(() => {
    if (podeDigitar && form.setor_id && !produtoPendente) {
      setTimeout(() => codigoInputRef.current?.focus(), 60);
    }
  }, [podeDigitar, form.setor_id, produtoPendente]);

  /* F9 global — alterna pra busca avançada (só durante digitação) */
  useEffect(() => {
    const h = e => {
      if (e.key === 'F3' && !modalProdutoDigitacao && !modalBusca) {
        e.preventDefault();
        setModalConsulta(v => !v);
      }
      if (e.key === 'F9' && podeDigitar && !modalConsulta && !modalBusca) {
        e.preventDefault();
        setModalProdutoDigitacao(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [modalProdutoDigitacao, modalBusca, modalConsulta, podeDigitar]);

  /* Carrega lista */
  const carregarLista = async () => {
    try {
      const { data } = await api.get('/estoque/manutencao');
      setLista(data);
      return data;
    } catch { return []; }
  };

  /* Carrega registro individual (consulta — sem edição) */
  const carregarManutencao = async (id, lst, pos) => {
    try {
      const { data } = await api.get(`/estoque/manutencao/${id}`);
      const itens = data.itens.map((it, i) => {
        // Contagens salvas antes da migração (ou lançamentos manuais antigos)
        // não têm o snapshot — mostra só o delta aplicado, sem "Estoque"/"Contada" reais.
        const temSnapshot = it.estoque_snapshot != null && it.quantidade_contada != null;
        const delta = it.operacao === 'entrada' ? parseFloat(it.quantidade) : -parseFloat(it.quantidade);
        return {
          _key:         it.id || i + 1,
          produto_id:   it.produto_id,
          codigo_prod:  it.codigo_interno || '',
          codigo_barras: it.codigo_barras || '',
          descricao:    it.descricao || '',
          estoque:      temSnapshot ? parseFloat(it.estoque_snapshot) : null,
          contada:      temSnapshot ? parseFloat(it.quantidade_contada) : delta,
          setor_id:     it.setor_id || '',
          setor_sigla:  it.setor_sigla || '',
          setor_nome:   it.setor_nome || '',
          data_hora:    it.criado_em,
          unidade_fraciona: it.unidade_fraciona || false,
          composto:     !!it.composto,
          preco_custo:  parseFloat(it.preco_custo) || 0,
          resolvidoComposto: null,
          // Lotes declarados por ESTE produto nesta manutenção (vêm à
          // parte, em data.lotes — desacoplados da quantidade/diferença do
          // item, ver utils/vencimentosContagem); entrada comum antiga
          // (Reposição/manual, um vencimento só) ainda pode vir no próprio
          // it.data_vencimento — mantido como fallback de leitura.
          vencimentos: (data.lotes || []).filter(l => l.produto_id === it.produto_id).length
            ? data.lotes.filter(l => l.produto_id === it.produto_id).map(l => ({ quantidade: l.quantidade, data_vencimento: l.data_vencimento }))
            : (it.data_vencimento ? [{ quantidade: it.quantidade, data_vencimento: it.data_vencimento }] : []),
          salvo: true,
        };
      });
      setForm({
        id:       data.id,
        data:     data.data ? data.data.slice(0, 10) : today(),
        setor_id: String(data.setor_id || ''),
        motivo:   data.motivo || '',
        itens,
      });
      setPosicao(pos);
    } catch { setErroSalvar('Erro ao carregar ajuste.'); }
  };

  useEffect(() => {
    carregarLista();
  }, []); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* Navegação */
  const navAnterior = () => {
    if (posicao > 0 && lista[posicao - 1]) {
      carregarManutencao(lista[posicao - 1].id, lista, posicao - 1);
    }
  };
  const navProximo = () => {
    if (posicao < lista.length - 1 && lista[posicao + 1]) {
      carregarManutencao(lista[posicao + 1].id, lista, posicao + 1);
    }
  };

  /* ── Contagem: código digitado/escaneado ── */
  const handleCodigoEnter = async () => {
    const cod = codigoInput.trim();
    if (!cod || !form.setor_id || buscandoCodigo) return;
    setBuscandoCodigo(true);
    try {
      let prod = await resolverProdutoPorCodigo(cod);
      prod = await confirmarReativacaoSeInativo(prod);
      if (!prod) {
        setNaoListados(nl => [{ codigo: cod, data_hora: new Date().toISOString() }, ...nl]);
        setCodigoInput('');
        setTimeout(() => codigoInputRef.current?.focus(), 30);
        return;
      }
      const estoqueAtual = await fetchEstoqueLocal(prod.id, form.setor_id);
      if (qtd1Auto) {
        commitContagem(prod, null, true, estoqueAtual);
        setCodigoInput('');
        setTimeout(() => codigoInputRef.current?.focus(), 30);
      } else {
        setProdutoPendente({ ...prod, _estoqueAtual: estoqueAtual ?? 0 });
        setSecaoPendente(form.setor_id);
        setTimeout(() => quantidadeInputRef.current?.focus(), 30);
      }
    } finally {
      setBuscandoCodigo(false);
    }
  };

  /* Produto escolhido via F9 (busca avançada) durante a digitação */
  const onProdutoDigitacaoSelect = async (produtoEscolhido) => {
    setModalProdutoDigitacao(false);
    const prod = await confirmarReativacaoSeInativo(produtoEscolhido);
    if (!prod) return; // recusou ativar — não adiciona nada
    const estoqueAtual = await fetchEstoqueLocal(prod.id, form.setor_id);
    if (qtd1Auto) {
      commitContagem(prod, null, true, estoqueAtual);
      setCodigoInput('');
      setTimeout(() => codigoInputRef.current?.focus(), 60);
    } else {
      setProdutoPendente({ ...prod, _estoqueAtual: estoqueAtual ?? 0 });
      setSecaoPendente(form.setor_id);
      setTimeout(() => quantidadeInputRef.current?.focus(), 60);
    }
  };

  /* Confirma a quantidade digitada pro produto pendente (modo manual) */
  const confirmarQuantidadePendente = () => {
    const qtd = parseFloat(quantidadeInput);
    if (!produtoPendente || !(qtd >= 0)) return;
    commitContagem(produtoPendente, qtd, false, produtoPendente._estoqueAtual);
    setProdutoPendente(null);
    setQuantidadeInput('');
    setCodigoInput('');
    setTimeout(() => codigoInputRef.current?.focus(), 30);
  };

  /* Insere/atualiza um item na grade de contados.
     incrementar=true (modo "Qtde 1 automático"): reescanear SOMA 1 na linha —
     é a leitora tipo "pistola" contando unidade física por unidade física.
     incrementar=false (quantidade digitada manualmente): reescanear SUBSTITUI
     a contagem da linha pelo valor novo — o operador está redeclarando o total. */
  const commitContagem = (prod, quantidadeDigitada, incrementar, estoqueAtual) => {
    const setorItem = solicitarSecao ? (secaoPendente || form.setor_id) : form.setor_id;
    setForm(f => {
      const idxExistente = f.itens.findIndex(it => it.produto_id === prod.id && !it.salvo);
      if (idxExistente >= 0) {
        const itens = f.itens.map((it, i) => {
          if (i !== idxExistente) return it;
          const novaContada = incrementar ? (it.contada + 1) : quantidadeDigitada;
          return { ...it, contada: novaContada, data_hora: new Date().toISOString(), setor_id: setorItem, resolvidoComposto: null };
        });
        return { ...f, itens };
      }
      const novoItem = {
        _key:         ++keySeq.current,
        produto_id:   prod.id,
        codigo_prod:  String(prod.codigo_interno || ''),
        codigo_barras: prod.codigo_barras || '',
        descricao:    prod.nome,
        unidade:      prod.unidade || 'UN',
        unidade_fraciona: prod.unidade_fraciona || false,
        composto:     !!prod.composto,
        preco_custo:  parseFloat(prod.preco_custo) || 0,
        estoque:      estoqueAtual ?? 0,
        contada:      incrementar ? 1 : quantidadeDigitada,
        setor_id:     setorItem,
        data_hora:    new Date().toISOString(),
        resolvidoComposto: null,
        vencimentos: [],
        salvo: false,
      };
      return { ...f, itens: [...f.itens, novoItem] };
    });
  };

  const setContadaManual = (idx, valor) => {
    const v = valor === '' ? '' : parseFloat(valor);
    setForm(f => ({ ...f, itens: f.itens.map((it, i) => i !== idx ? it : { ...it, contada: v }) }));
  };

  // Só faz sentido informar vencimento quando a diferença é de ENTRADA
  // (contada > estoque) — reaproveitado pelo campo, que só aparece nesse
  // caso; ver utils/vencimentosContagem.js (o item pode virar mais de um
  // no POST, um por vencimento detalhado).
  const setVencimentosItem = (idx, vencimentos) => {
    setForm(f => ({ ...f, itens: f.itens.map((it, i) => i !== idx ? it : { ...it, vencimentos }) }));
  };

  const handleRemoverItem = idx => {
    setForm(f => ({ ...f, itens: f.itens.filter((_, i) => i !== idx) }));
  };

  const abrirResolverComposto = idx => setModalResolverComposto({ idx });

  const handleResolverComposto = async (modo) => {
    const idx = modalResolverComposto.idx;
    const item = form.itens[idx];
    const diferenca = item.contada - item.estoque;
    if (modo === 'direto') {
      setModalResolverComposto(null);
      return;
    }
    const endpoint = modo === 'montar' ? `/produtos/${item.produto_id}/montar` : `/produtos/${item.produto_id}/desmontar`;
    await api.post(endpoint, { quantidade: Math.abs(diferenca), setor_id: item.setor_id || form.setor_id || null });
    setForm(f => ({
      ...f,
      itens: f.itens.map((it, i) => i !== idx ? it : { ...it, estoque: it.contada, resolvidoComposto: modo }),
    }));
    setModalResolverComposto(null);
  };

  /* CRUD */
  const handleNovo = () => {
    setForm({ ...FORM_VAZIO, data: today() });
    setModo('novo');
    setErroSalvar('');
    setNaoListados([]);
    setAbaContagem('contados');
  };

  // Reabre uma contagem já confirmada pra correção — permissão real (perfil
  // admin/gerente OU flag altera_manutencao_estoque) e senha só são checadas
  // de fato no backend ao salvar; aqui só destrava a grade pra edição (nada é
  // gravado ainda, então não custa liberar a visualização/edição local).
  const handleAlterar = () => {
    if (!form.id) return;
    setForm(f => ({ ...f, itens: f.itens.map(it => ({ ...it, salvo: false })) }));
    setModo('alterar');
    setErroSalvar('');
    setNaoListados([]);
    setAbaContagem('contados');
  };

  const handleCancelar = async () => {
    setErroSalvar('');
    setProdutoPendente(null);
    setNaoListados([]);
    if (modo === 'alterar' && form.id) {
      await carregarManutencao(form.id, lista, posicao);
    } else {
      setForm({ ...FORM_VAZIO });
    }
    setModo('nav');
  };

  const handleSalvar = async () => {
    if (!form.setor_id) { setErroSalvar('Selecione o Local de Estoque.'); return; }
    if (!form.motivo.trim()) { setErroSalvar('Motivo é obrigatório.'); return; }
    const comDiferenca = form.itens.filter(it => !it.resolvidoComposto && (it.contada - it.estoque) !== 0);

    if (!comDiferenca.length) {
      // Nada a ajustar (tudo bateu ou já foi resolvido via montagem/desmontagem)
      setForm({ ...FORM_VAZIO });
      setModo('nav');
      setErroSalvar('');
      alert('Contagem concluída — nenhuma diferença a ajustar.');
      return;
    }

    setSalvando(true);
    setErroSalvar('');
    try {
      const payload = {
        data:     form.data,
        setor_id: form.setor_id ? parseInt(form.setor_id) : null,
        motivo:   form.motivo,
        itens:    comDiferenca.flatMap(it => expandirItemContagem(it, form.setor_id)),
      };
      const { data: res } = await api.post('/estoque/manutencao', payload);
      const novaLista = await carregarLista();
      const pos = novaLista.findIndex(m => m.id === res.id);
      await carregarManutencao(res.id, novaLista, pos >= 0 ? pos : 0);
      setModo('nav');
    } catch (err) {
      setErroSalvar(err.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarAlteracao = async () => {
    if (!form.setor_id) { setErroSalvar('Selecione o Local de Estoque.'); return; }
    if (!form.motivo.trim()) { setErroSalvar('Motivo é obrigatório.'); return; }
    const comDiferenca = form.itens.filter(it => !it.resolvidoComposto && (it.contada - it.estoque) !== 0);
    if (!comDiferenca.length) {
      setErroSalvar('Nenhuma diferença a gravar — se quer desfazer tudo, use Excluir em vez de Alterar.');
      return;
    }

    const senha = await pedirSenhaAdmin(
      'Alterar uma contagem de estoque já confirmada exige senha de administrador ou gerente.',
      { acao: 'alterar_manutencao_estoque', detalhes: `Manutenção #${form.id} — ${form.motivo}` }
    );
    if (senha === null) return;

    setSalvando(true);
    setErroSalvar('');
    try {
      const payload = {
        data:     form.data,
        setor_id: form.setor_id ? parseInt(form.setor_id) : null,
        motivo:   form.motivo,
        senha_admin: senha,
        itens:    comDiferenca.flatMap(it => expandirItemContagem(it, form.setor_id)),
      };
      await api.put(`/estoque/manutencao/${form.id}`, payload);
      const novaLista = await carregarLista();
      const pos = novaLista.findIndex(m => m.id === form.id);
      await carregarManutencao(form.id, novaLista, pos >= 0 ? pos : posicao);
      setModo('nav');
    } catch (err) {
      setErroSalvar(err.response?.data?.erro || 'Erro ao salvar a alteração.');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async () => {
    if (!form.id) return;
    setConfirmarExclusao(false);
    try {
      await api.delete(`/estoque/manutencao/${form.id}`);
      const novaLista = await carregarLista();
      if (novaLista.length > 0) {
        const pos = Math.min(posicao, novaLista.length - 1);
        await carregarManutencao(novaLista[pos].id, novaLista, pos);
      } else {
        setForm({ ...FORM_VAZIO });
        setPosicao(0);
      }
      setModo('nav');
    } catch (err) {
      setErroSalvar(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  const statusText = () => {
    if (erroSalvar) return <span className={s.statusErro}>{erroSalvar}</span>;
    if (modo === 'novo')   return <span className={s.statusNovo}>Nova contagem — escaneie/digite os produtos e clique em Salvar.</span>;
    if (modo === 'alterar') return <span className={s.statusNovo}>Alterando contagem #{form.id} — corrija os itens e clique em Salvar Alteração (exige senha admin/gerente).</span>;
    if (form.id) return `Ajuste #${form.id} — ${fmtData(form.data)} — ${form.motivo}`;
    return 'Nenhum ajuste selecionado. Clique em Novo ou Buscar.';
  };

  // Totais do rodapé (linhas com diferença, já resolvidas via montagem ficam de fora)
  const linhasComDiferenca = form.itens.filter(it => !it.resolvidoComposto && (it.contada - it.estoque) !== 0);
  const totalItensContados = form.itens.length;
  const totalDiferencaValor = linhasComDiferenca.reduce(
    (acc, it) => acc + (it.contada - it.estoque) * (it.preco_custo || 0), 0
  );

  return (
    <div className={s.erpWrap}>
      {/* Barra de título */}
      <div className={s.titleBar}>
        Manutenção de Estoque — Contagem
      </div>

      {/* Cabeçalho */}
      <div className={s.headerRow}>
        <div className={s.headerGroup}>
          <span className={s.headerLabel}>Data</span>
          <input className={s.inputErp} type="date" style={{ width: 120 }}
            value={form.data} readOnly={!editando}
            onChange={e => set('data', e.target.value)} />
        </div>
        <div className={s.headerDivider} />
        <div className={s.headerGroup}>
          <span className={s.headerLabel}>Local de Estoque</span>
          <select className={s.selectErp} style={{ width: 220 }}
            value={form.setor_id} disabled={!editando || form.itens.some(it => !it.salvo)}
            onChange={e => set('setor_id', e.target.value)}>
            <option value="">— selecione —</option>
            {setoresLista.map(se => (
              <option key={se.id} value={se.id}>{se.sigla} — {se.nome}</option>
            ))}
          </select>
        </div>
        <div className={s.headerDivider} />
        <div className={s.headerGroup} style={{ flex: 1 }}>
          <span className={s.headerLabel}>Motivo do Ajuste</span>
          <input className={s.inputErp} style={{ flex: 1, minWidth: 200 }}
            value={form.motivo} readOnly={!editando}
            placeholder={editando ? 'Descreva o motivo do ajuste...' : ''}
            onChange={e => set('motivo', e.target.value)} />
        </div>
      </div>

      {/* Painel de digitação (só em modo Novo) */}
      {podeDigitar && (
        <div style={{ background: 'var(--content-bg)', border: '1px solid var(--border)', borderRadius: 6,
          padding: '10px 14px', margin: '10px 16px 0' }}>
          {!form.setor_id ? (
            <p style={{ margin: 0, color: '#888', fontSize: 12.5 }}>
              Selecione o <strong>Local de Estoque</strong> acima para começar a contagem.
            </p>
          ) : produtoPendente ? (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ maxWidth: 460, marginRight: 24 }}>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>PRODUTO</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {produtoPendente.codigo_interno ? `${produtoPendente.codigo_interno} — ` : ''}
                  {produtoPendente.nome}{produtoPendente.composto ? ' 🧩' : ''}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  UN: {produtoPendente.unidade || '—'} · Estoque no local:{' '}
                  <strong>{fmtQtd(produtoPendente._estoqueAtual, produtoPendente.unidade_fraciona)}</strong>
                </div>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Quantidade Contada</span>
                <input ref={quantidadeInputRef} className={s.inputErp} style={{ width: 110 }}
                  type="number" min="0" step={produtoPendente.unidade_fraciona ? '0.001' : '1'}
                  value={quantidadeInput}
                  onChange={e => setQuantidadeInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && confirmarQuantidadePendente()} />
              </label>
              {solicitarSecao && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Seção</span>
                  <select className={s.selectErp} style={{ width: 160 }}
                    value={secaoPendente} onChange={e => setSecaoPendente(e.target.value)}>
                    {setoresLista.map(se => (
                      <option key={se.id} value={se.id}>{se.sigla} — {se.nome}</option>
                    ))}
                  </select>
                </label>
              )}
              <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={confirmarQuantidadePendente} disabled={!(parseFloat(quantidadeInput) >= 0)}>
                ✓ OK
              </button>
              <button className={s.btnErp}
                onClick={() => { setProdutoPendente(null); setQuantidadeInput(''); setTimeout(() => codigoInputRef.current?.focus(), 30); }}>
                Cancelar
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>
                  Código / Código de Barras <span style={{ fontWeight: 400, color: '#999' }}>(F9 pesquisa por nome)</span>
                </span>
                <input ref={codigoInputRef} className={s.inputErp} style={{ width: 240 }}
                  placeholder="Escaneie ou digite e pressione Enter…"
                  value={codigoInput} disabled={buscandoCodigo}
                  onChange={e => setCodigoInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCodigoEnter()} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, paddingBottom: 6 }}>
                <input type="checkbox" checked={qtd1Auto} onChange={e => setQtd1Auto(e.target.checked)} />
                Qtde 1 automático <span style={{ color: '#999' }}>(cada leitura soma 1 na contagem)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, paddingBottom: 6 }}>
                <input type="checkbox" checked={solicitarSecao} onChange={e => setSolicitarSecao(e.target.checked)} />
                Solicitar Seção a cada Item
              </label>
              {buscandoCodigo && <span style={{ fontSize: 12, color: '#888' }}>buscando…</span>}
            </div>
          )}
        </div>
      )}

      {/* Abas Contados / Não Listados (só em modo Novo) */}
      {podeDigitar && (
        <div style={{ display: 'flex', gap: 0, margin: '10px 16px 0', borderBottom: '2px solid #1464c8' }}>
          {[['contados', `Produtos Contados (${form.itens.length})`], ['nao_listados', `Produtos Não Listados (${naoListados.length})`]].map(([id, label]) => (
            <button key={id} onClick={() => setAbaContagem(id)}
              style={{
                padding: '5px 14px', border: 'none', borderRight: '1px solid #bbb',
                background: abaContagem === id ? '#fff' : '#e0e0e0',
                fontWeight: abaContagem === id ? 'bold' : 'normal',
                color: abaContagem === id ? '#0a3a8c' : '#333',
                cursor: 'pointer', fontSize: 12.5,
              }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Conteúdo */}
      <div className={s.tabContent}>
        {(!podeDigitar || abaContagem === 'contados') && (
          <table className={s.itensGrid}>
            <thead>
              <tr>
                <th className={s.tdSeq}>#</th>
                <th className={s.tdCod}>Código</th>
                <th style={{ minWidth: 180 }}>Descrição</th>
                <th style={{ width: 100 }}>Barra</th>
                <th style={{ width: 80, textAlign: 'right' }}>Estoque</th>
                <th style={{ width: 90, textAlign: 'right' }}>Contada</th>
                <th style={{ width: 90, textAlign: 'right' }}>Diferença</th>
                <th style={{ width: 120 }}>Vencimento</th>
                <th style={{ width: 90 }}>Seção</th>
                <th style={{ width: 130 }}>Data/Hora</th>
                {podeDigitar && <th className={s.tdDel}></th>}
              </tr>
            </thead>
            <tbody>
              {form.itens.length === 0 && (
                <tr>
                  <td colSpan={podeDigitar ? 11 : 10}
                    style={{ textAlign: 'center', color: '#888', padding: 20, fontStyle: 'italic' }}>
                    {podeDigitar ? 'Escaneie ou digite um código acima para começar a contagem.' : 'Nenhum item.'}
                  </td>
                </tr>
              )}
              {form.itens.map((item, idx) => {
                const diferenca = (item.contada === '' || item.contada == null || item.estoque == null)
                  ? null : item.contada - item.estoque;
                const corDiferenca = diferenca == null ? '#999' : diferenca > 0 ? '#006600' : diferenca < 0 ? '#c00000' : '#666';
                return (
                  <tr key={item._key}>
                    <td className={s.tdSeq}>{idx + 1}</td>
                    <td className={s.tdCod} style={{ fontFamily: 'monospace' }}>{item.codigo_prod}</td>
                    <td>{item.descricao}{item.composto ? ' 🧩' : ''}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{item.codigo_barras || '—'}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtQtd(item.estoque, item.unidade_fraciona)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {podeDigitar && !item.salvo ? (
                        <input className={`${s.cellInput} ${s.cellInputNum}`} type="number" min="0"
                          step={item.unidade_fraciona ? '0.001' : '1'}
                          value={item.contada}
                          onChange={e => setContadaManual(idx, e.target.value)} />
                      ) : fmtQtd(item.contada, item.unidade_fraciona)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: corDiferenca }}>
                      {diferenca == null ? '—' : (diferenca > 0 ? '+' : '') + fmtQtd(diferenca, item.unidade_fraciona)}
                    </td>
                    <td>
                      {diferenca > 0 ? (
                        podeDigitar && !item.salvo ? (
                          <button className={s.btnErp} style={{ padding: '2px 6px', fontSize: 11 }}
                            onClick={() => setModalVencimentos({ idx })}
                            title="Opcional — divide a diferença em um ou mais vencimentos, cria os lotes em Lotes/Validades">
                            {item.vencimentos?.length
                              ? (item.vencimentos.length === 1
                                ? fmtData(item.vencimentos[0].data_vencimento)
                                : `${item.vencimentos.length} vencimentos`)
                              : '📅 Vencimento'}
                          </button>
                        ) : (item.vencimentos?.length
                          ? (item.vencimentos.length === 1
                            ? fmtData(item.vencimentos[0].data_vencimento)
                            : `${item.vencimentos.length} vencimentos`)
                          : '—')
                      ) : '—'}
                    </td>
                    <td>
                      {setoresLista.find(se => String(se.id) === String(item.setor_id))?.sigla
                        || item.setor_sigla || '—'}
                    </td>
                    <td style={{ fontSize: 11, color: '#666' }}>{fmtHora(item.data_hora)}</td>
                    {podeDigitar && (
                      <td className={s.tdDel} style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                        {item.composto && diferenca && !item.resolvidoComposto && (
                          <button className={s.btnErp} style={{ padding: '1px 5px', fontSize: 11 }}
                            title="Resolver diferença de composto via montagem/desmontagem"
                            onClick={() => abrirResolverComposto(idx)}>🔧</button>
                        )}
                        {item.resolvidoComposto && (
                          <span title={item.resolvidoComposto === 'montar' ? 'Resolvido via montagem' : 'Resolvido via desmontagem'}
                            style={{ fontSize: 11, color: '#006600' }}>✓</span>
                        )}
                        <button className={`${s.btnErp} ${s.btnErpPerigo} ${s.btnErpSmall}`}
                          onClick={() => handleRemoverItem(idx)}>✕</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {podeDigitar && abaContagem === 'nao_listados' && (
          <table className={s.itensGrid}>
            <thead>
              <tr>
                <th className={s.tdSeq}>#</th>
                <th>Código digitado</th>
                <th style={{ width: 160 }}>Data/Hora</th>
              </tr>
            </thead>
            <tbody>
              {naoListados.length === 0 && (
                <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', padding: 20, fontStyle: 'italic' }}>
                  Nenhum código não encontrado nesta sessão.
                </td></tr>
              )}
              {naoListados.map((nl, i) => (
                <tr key={i}>
                  <td className={s.tdSeq}>{i + 1}</td>
                  <td style={{ fontFamily: 'monospace' }}>{nl.codigo}</td>
                  <td style={{ fontSize: 11, color: '#666' }}>{fmtHora(nl.data_hora)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Resumo */}
        {form.itens.length > 0 && (
          <div className={s.itensTotais}>
            <div className={s.totaisBox}>
              <div className={s.totaisLinha}>
                <span>Total de itens contados:</span>
                <strong>{totalItensContados}</strong>
              </div>
              <div className={s.totaisLinha}>
                <span>Itens com diferença:</span>
                <strong>{linhasComDiferenca.length}</strong>
              </div>
              <div className={s.totaisLinha}>
                <span>Valor da diferença (custo):</span>
                <strong style={{ color: totalDiferencaValor >= 0 ? '#006600' : '#c00000' }}>
                  {fmtMoeda(totalDiferencaValor)}
                </strong>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className={s.statusBar}>{statusText()}</div>

      {/* Rodapé */}
      <div className={s.footer}>
        <div className={s.footerLeft}>
          <button className={`${s.btnErp} ${s.btnErpNav}`}
            onClick={navAnterior} disabled={editando || posicao === 0}>&lt;</button>
          <span className={s.footerPos}>
            {lista.length ? `${posicao + 1} / ${lista.length}` : '0 / 0'}
          </span>
          <button className={`${s.btnErp} ${s.btnErpNav}`}
            onClick={navProximo} disabled={editando || posicao >= lista.length - 1}>&gt;</button>
          <span className={s.f3Hint} onClick={() => setModalConsulta(true)} title="Consulta de produtos (F3)">
            F3 – Consulta Produtos
          </span>
        </div>
        <div className={s.footerRight}>
          <button className={s.btnErp} onClick={handleNovo} disabled={editando}>Novo</button>
          <button className={s.btnErp} onClick={() => setModalBusca(true)} disabled={editando}>Buscar</button>
          <button className={s.btnErp} onClick={handleAlterar} disabled={editando || !form.id}
            title="Reabre esta contagem pra correção — exige senha de administrador ou gerente ao salvar">
            Alterar
          </button>
          <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>Cancelar</button>
          <button className={`${s.btnErp} ${s.btnErpPerigo}`}
            onClick={() => setConfirmarExclusao(true)}
            disabled={editando || !form.id}>
            Excluir
          </button>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            onClick={modo === 'alterar' ? handleSalvarAlteracao : handleSalvar}
            disabled={!editando || salvando}>
            {salvando ? 'Salvando...' : modo === 'alterar' ? 'Salvar Alteração' : 'Salvar'}
          </button>
          <div className={s.footerSep} />
          <button className={s.btnErp} onClick={() => window.history.back()}>Sair</button>
        </div>
      </div>

      {/* Modais */}
      {modalBusca && (
        <ModalBuscaManutencao
          onSelect={async m => {
            const i = lista.findIndex(l => l.id === m.id);
            await carregarManutencao(m.id, lista, i >= 0 ? i : posicao);
            setModalBusca(false);
          }}
          onClose={() => setModalBusca(false)}
        />
      )}

      {modalConsulta && (
        <ModalBuscaProduto consultaOnly onSelect={() => {}} onClose={() => setModalConsulta(false)} />
      )}

      {modalProdutoDigitacao && (
        <ModalBuscaProduto onSelect={onProdutoDigitacaoSelect} onClose={() => setModalProdutoDigitacao(false)} />
      )}

      {modalResolverComposto && (
        <ModalResolverComposto
          item={form.itens[modalResolverComposto.idx]}
          onResolver={handleResolverComposto}
          onClose={() => setModalResolverComposto(null)}
        />
      )}

      {modalVencimentos && (
        <ModalVencimentos
          item={form.itens[modalVencimentos.idx]}
          onSalvar={(vencimentos) => { setVencimentosItem(modalVencimentos.idx, vencimentos); setModalVencimentos(null); }}
          onClose={() => setModalVencimentos(null)}
        />
      )}

      {/* Confirmação excluir */}
      {confirmarExclusao && (
        <div className={s.overlay}>
          <div className={s.modal} style={{ maxWidth: 420 }}>
            <div className={s.modalTitle}>Confirmar Exclusão</div>
            <div className={s.modalBody}>
              <p style={{ margin: 0 }}>
                Excluir o ajuste <strong>#{form.id}</strong>?<br />
                Os movimentos de estoque serão <strong>revertidos automaticamente</strong>.
              </p>
            </div>
            <div className={s.modalFooter}>
              <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={handleExcluir}>Excluir e Reverter</button>
              <button className={s.btnErp} onClick={() => setConfirmarExclusao(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
